import { jsPDF } from 'jspdf';
import {
  htmlToBlocks,
  hexToRgb,
  sectionHex,
  sectionLabel,
  tint,
  formatExportDate,
  type RichBlock,
  type InlineRun,
  type SermonElement,
  type SermonMeta,
} from './richText';

// ─── Design tokens ────────────────────────────────────────────────────────────
// All colors as [R, G, B] tuples used across every export.
type RGB = [number, number, number];

const INK:   RGB = [15, 23, 42];      // near-black body text
const DIM:   RGB = [71, 85, 105];     // secondary text
const MUTED: RGB = [148, 163, 184];   // captions, footers
const BRAND: RGB = [30, 64, 175];     // brand deep blue
const RULE:  RGB = [226, 232, 240];   // divider lines

function applyFill(doc: jsPDF, c: RGB)  { doc.setFillColor(c[0], c[1], c[2]); }
function applyDraw(doc: jsPDF, c: RGB)  { doc.setDrawColor(c[0], c[1], c[2]); }
function applyColor(doc: jsPDF, c: RGB) { doc.setTextColor(c[0], c[1], c[2]); }

interface PDFTitleOptions {
  title: string;
  subtitle?: string;
  date?: string;
}

// ─── createStyledPDF ──────────────────────────────────────────────────────────
// Renders a clean document header and returns the Y position to start content.
// Used by SermonSummarizer, SermonTranslator, and ServiceAssets.
export function createStyledPDF(options: PDFTitleOptions, doc?: jsPDF): number {
  if (!options?.title) throw new Error('PDF title is required');
  if (!doc) doc = new jsPDF();

  const W = doc.internal.pageSize.width;
  const ml = 20;
  let y = 20;

  // Thin brand rule at top
  applyDraw(doc, BRAND);
  doc.setLineWidth(1.2);
  doc.line(ml, y, W - ml, y);
  y += 10;

  // Title
  applyColor(doc, INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(String(options.title), W / 2, y, { align: 'center' });
  y += 10;

  if (options.subtitle) {
    applyColor(doc, DIM);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.text(String(options.subtitle), W / 2, y, { align: 'center' });
    y += 7;
  }

  if (options.date) {
    applyColor(doc, MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text(String(options.date), W / 2, y, { align: 'center' });
    y += 7;
  }

  // Thin divider
  y += 3;
  applyDraw(doc, RULE);
  doc.setLineWidth(0.4);
  doc.line(ml, y, W - ml, y);
  y += 10;

  // Reset state
  applyColor(doc, INK);
  return y;
}

// ─── addSection ───────────────────────────────────────────────────────────────
// Renders a section with a left accent bar header + body text.
// Used by all tool-based PDF exports.
export function addSection(doc: jsPDF, title: string, content: string | string[], startY: number): number {
  if (!doc) throw new Error('jsPDF document instance is required');

  const ml = 20;
  const W = doc.internal.pageSize.width;
  const H = doc.internal.pageSize.height;
  const contentW = W - ml * 2;
  const bottomMargin = 28;
  const lineH = 6.5;
  let y = startY;

  if (title?.trim()) {
    if (y > H - bottomMargin - 24) { doc.addPage(); y = ml; }

    // Left accent bar
    applyFill(doc, BRAND);
    doc.rect(ml - 5, y - 5.5, 3, 11, 'F');

    // Section label
    applyColor(doc, INK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(String(title).trim(), ml + 1, y);
    y += 8;

    // Underrule
    applyDraw(doc, RULE);
    doc.setLineWidth(0.3);
    doc.line(ml, y - 2, W - ml, y - 2);
    y += 5;
  }

  applyColor(doc, INK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  const contentArray = Array.isArray(content) ? content.map(String) : [String(content)];

  for (let i = 0; i < contentArray.length; i++) {
    const raw = contentArray[i];
    if (!raw?.trim()) continue;

    const isBullet = raw.trim().startsWith('•') || raw.trim().startsWith('-');
    const indent = isBullet ? 6 : 0;
    const clean = raw.trim().replace(/^\s*[-•]\s*/, '');
    const text = isBullet ? `• ${clean}` : clean;
    const lines = doc.splitTextToSize(text, contentW - indent);
    if (!lines?.length) continue;

    const needed = lines.length * lineH + 6;
    if (y + needed > H - bottomMargin) { doc.addPage(); y = ml; }

    for (let j = 0; j < lines.length; j++) {
      if (typeof lines[j] === 'string' && lines[j].trim()) {
        doc.text(lines[j], ml + (isBullet && j > 0 ? indent + 4 : indent), y);
        y += lineH;
      }
    }

    const next = contentArray[i + 1];
    const nextIsBullet = next?.trim().startsWith('•') || next?.trim().startsWith('-');
    y += (isBullet && nextIsBullet) ? 2 : (i < contentArray.length - 1 ? 5 : 10);
  }

  return y;
}

// ─── Text cleaning helpers ────────────────────────────────────────────────────
export function cleanAIText(text: string | null | undefined): string {
  if (!text) return '';
  let cleaned = text.replace(/^(AI:|Assistant:|ChatGPT:|Bot:)\s*/i, '');
  cleaned = cleaned.replace(/[*_~`]/g, '');
  cleaned = cleaned.replace(/^\s*[\-\*]\s+/gm, '• ');
  cleaned = cleaned.trim().replace(/\s+/g, ' ');
  return cleaned || '';
}

export function cleanAIArray(items: (string | null | undefined)[]): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => cleanAIText(item))
    .filter((item): item is string => typeof item === 'string' && item.length > 0);
}

// ─── Rich text flow engine ────────────────────────────────────────────────────
// Lays out styled inline runs word-by-word so inline marks (bold, italic,
// underline, strike, color, highlight) survive and text wraps without running
// together. Returns the Y position after the last drawn line.
interface FlowOptions {
  x0: number;
  xMax: number;
  size: number;
  lineH: number;
  color: RGB;
  forceBold?: boolean;
  topY: number;
  bottomY: number;
  draw?: boolean;     // false = measure only (no drawing, no page breaks)
}

function setRunFont(doc: jsPDF, run: InlineRun, size: number, forceBold?: boolean) {
  const bold = run.bold || forceBold;
  const style = bold && run.italic ? 'bolditalic' : bold ? 'bold' : run.italic ? 'italic' : 'normal';
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
}

// Lays styled runs word-by-word. `startY` is the BASELINE of the first line.
// Returns the baseline of the LAST line drawn (callers add lineH to move on).
function flowRuns(doc: jsPDF, runs: InlineRun[], startY: number, o: FlowOptions): number {
  const draw = o.draw !== false;
  let x = o.x0;
  let y = startY;
  const newline = () => {
    x = o.x0;
    y += o.lineH;
    if (draw && y > o.bottomY) { doc.addPage(); y = o.topY; }
  };

  for (const run of runs) {
    if (!run.text) continue;
    const highlightRgb = run.highlight ? hexToRgb(run.highlight) : null;
    const textRgb = run.color ? hexToRgb(run.color) : o.color;
    const segments = run.text.split('\n');

    segments.forEach((seg, si) => {
      if (si > 0) newline();
      if (seg === '') return;
      setRunFont(doc, run, o.size, o.forceBold);
      const spaceW = doc.getTextWidth(' ');
      const tokens = seg.split(/(\s+)/);

      for (const tok of tokens) {
        if (tok === '') continue;
        if (/^\s+$/.test(tok)) { if (x > o.x0) x += spaceW; continue; }

        setRunFont(doc, run, o.size, o.forceBold);
        const w = doc.getTextWidth(tok);
        if (x + w > o.xMax && x > o.x0) newline();

        if (draw) {
          if (highlightRgb) {
            doc.setFillColor(highlightRgb[0], highlightRgb[1], highlightRgb[2]);
            doc.rect(x - 0.5, y - o.size * 0.74, w + 1, o.size * 0.96, 'F');
          }
          setRunFont(doc, run, o.size, o.forceBold);
          doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
          doc.text(tok, x, y);
          if (run.underline || run.link) {
            doc.setDrawColor(textRgb[0], textRgb[1], textRgb[2]);
            doc.setLineWidth(0.5);
            doc.line(x, y + 1.6, x + w, y + 1.6);
          }
          if (run.strike) {
            doc.setDrawColor(textRgb[0], textRgb[1], textRgb[2]);
            doc.setLineWidth(0.5);
            doc.line(x, y - o.size * 0.28, x + w, y - o.size * 0.28);
          }
        }
        x += w;
      }
    });
  }
  return y;
}

interface BlockCtx {
  ml: number;
  mr: number;
  topY: number;
  bottomY: number;
  draw?: boolean;
}

// Renders one block and returns the BASELINE position for the next block
// (= last line baseline + one line height + paragraph spacing).
function renderBlock(doc: jsPDF, b: RichBlock, startY: number, ctx: BlockCtx): number {
  const { ml, mr, topY, bottomY } = ctx;
  const draw = ctx.draw !== false;
  let y = startY;

  if (b.kind === 'heading') {
    const size = b.level === 1 ? 16 : b.level === 2 ? 13.5 : 12;
    const lineH = size * 1.34;
    y += b.level <= 2 ? 8 : 5;            // extra lead before headings
    if (draw && y + lineH > bottomY) { doc.addPage(); y = topY; }
    y = flowRuns(doc, b.runs, y, { x0: ml, xMax: mr, size, lineH, color: INK, forceBold: true, topY, bottomY, draw });
    return y + lineH + 4;
  }

  if (b.kind === 'blockquote') {
    const lineH = 16;
    y += 4;
    if (draw && y + lineH > bottomY) { doc.addPage(); y = topY; }
    const x0 = ml + 16;
    const startPage = doc.getNumberOfPages();
    const startQy = y;
    const quoteRuns = b.runs.map(r => ({ ...r, italic: true }));
    y = flowRuns(doc, quoteRuns, y, { x0, xMax: mr - 8, size: 11, lineH, color: DIM, topY, bottomY, draw });
    if (draw && doc.getNumberOfPages() === startPage) {
      applyFill(doc, BRAND);
      doc.rect(ml + 4, startQy - 8, 3, (y - startQy) + 11, 'F');
    }
    return y + lineH + 6;
  }

  if (b.kind === 'bullet' || b.kind === 'ordered') {
    const lineH = 15.5;
    if (draw && y + lineH > bottomY) { doc.addPage(); y = topY; }
    const indentPer = 16;
    const baseX = ml + b.level * indentPer;
    const marker = b.kind === 'ordered' ? `${b.ordinal}.` : '•';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const markerW = doc.getTextWidth(`${marker} `);
    if (draw) {
      applyColor(doc, b.kind === 'ordered' ? INK : BRAND);
      doc.text(marker, baseX, y);
    }
    y = flowRuns(doc, b.runs, y, { x0: baseX + markerW, xMax: mr, size: 11, lineH, color: INK, topY, bottomY, draw });
    return y + lineH + 3;
  }

  // paragraph
  const lineH = 16;
  if (draw && y + lineH > bottomY) { doc.addPage(); y = topY; }
  y = flowRuns(doc, b.runs, y, { x0: ml, xMax: mr, size: 11, lineH, color: INK, topY, bottomY, draw });
  return y + lineH + 5;
}

// Measures the total vertical height a set of blocks will occupy, without
// drawing or paging — used to lay out section cards (background behind text).
function measureBlocks(doc: jsPDF, blocks: RichBlock[], startY: number, ctx: BlockCtx): number {
  const measureCtx: BlockCtx = { ...ctx, draw: false, bottomY: Number.POSITIVE_INFINITY };
  let y = startY;
  blocks.forEach(b => { y = renderBlock(doc, b, y, measureCtx); });
  return y - startY;
}

// ─── createSermonPDF ──────────────────────────────────────────────────────────
// Full sermon export: professional header, color-coded section banners, rich
// body text (highlights/colors/formatting preserved), clean footer.
export function createSermonPDF(sermon: SermonMeta, processedContent: SermonElement[]): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const ml = 58;     // left margin (~0.8 in)
  const mr = W - ml; // right margin
  const contentW = mr - ml;
  const topY = 54;
  const bottomY = H - 52;
  let y = topY;

  const ctx: BlockCtx = { ml, mr, topY, bottomY };

  // ── HEADER ──────────────────────────────────────────────────────────────────
  applyDraw(doc, BRAND);
  doc.setLineWidth(2.5);
  doc.line(ml, 28, mr, 28);

  applyColor(doc, INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  const titleLines = doc.splitTextToSize(sermon.title || 'Sermon', contentW);
  doc.text(titleLines, W / 2, y, { align: 'center' });
  y += titleLines.length * 18 + 6;

  if (sermon.scripture) {
    applyColor(doc, DIM);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(13);
    const sLines = doc.splitTextToSize(sermon.scripture, contentW);
    doc.text(sLines, W / 2, y, { align: 'center' });
    y += sLines.length * 15;
  }

  const dateStr = formatExportDate(sermon.date);
  if (dateStr) {
    applyColor(doc, MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(dateStr, W / 2, y, { align: 'center' });
    y += 12;
  }

  y += 6;
  applyDraw(doc, RULE);
  doc.setLineWidth(0.6);
  doc.line(ml, y, mr, y);
  y += 22;

  // ── CONTENT ─────────────────────────────────────────────────────────────────
  const cardLeft = ml - 16;
  const cardWidth = contentW + 32;
  const bannerH = 24;
  const headBaseOff = 16;  // label baseline below card top
  const bodyTopOff = 18;   // first body baseline below banner

  // Draws the colored header band (label + attribution) at a given top edge.
  const drawSectionHeader = (top: number, accent: RGB, headerTint: RGB, label: string, attribution?: string) => {
    doc.setFillColor(headerTint[0], headerTint[1], headerTint[2]);
    doc.rect(cardLeft, top, cardWidth, bannerH, 'F');
    doc.setFillColor(accent[0], accent[1], accent[2]);
    doc.rect(cardLeft, top, 4, bannerH, 'F');
    doc.setTextColor(accent[0], accent[1], accent[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(label, ml, top + headBaseOff);
    if (attribution) {
      applyColor(doc, DIM);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.text(`— ${attribution}`, mr, top + headBaseOff, { align: 'right' });
    }
  };

  processedContent.forEach((element) => {
    if (element.type === 'section') {
      const hex = sectionHex(element.sectionType);
      const accent = hexToRgb(hex);
      const bodyTint = hexToRgb(tint(hex, 0.95));
      const headerTint = hexToRgb(tint(hex, 0.85));
      const label = sectionLabel(element).toUpperCase();
      const blocks = htmlToBlocks(element.content);

      y += 10; // space before the card

      // Measure body so the card background can be drawn behind the text.
      const measuredBody = measureBlocks(doc, blocks, 0, ctx);
      const cardHeight = bannerH + bodyTopOff + measuredBody - 4;

      if (y + cardHeight <= bottomY) {
        // Fits on the current page.
      } else if (cardHeight <= bottomY - topY) {
        doc.addPage(); y = topY + 6; // fits on a fresh page
      } else {
        // Too tall for one page: header band + flowing body with real page breaks.
        drawSectionHeader(y, accent, headerTint, label, element.attribution);
        y += bannerH + bodyTopOff;
        blocks.forEach(b => { y = renderBlock(doc, b, y, ctx); });
        y += 16;
        return;
      }

      const cardTop = y;
      // Card background, then header band on top, then body text.
      doc.setFillColor(bodyTint[0], bodyTint[1], bodyTint[2]);
      doc.rect(cardLeft, cardTop, cardWidth, cardHeight, 'F');
      doc.setFillColor(accent[0], accent[1], accent[2]);
      doc.rect(cardLeft, cardTop, 4, cardHeight, 'F');
      drawSectionHeader(cardTop, accent, headerTint, label, element.attribution);

      y = cardTop + bannerH + bodyTopOff;
      blocks.forEach(b => { y = renderBlock(doc, b, y, ctx); });

      y = cardTop + cardHeight + 14;
    } else {
      const blocks = htmlToBlocks(element.content);
      blocks.forEach(b => { y = renderBlock(doc, b, y, ctx); });
      y += 4;
    }
  });

  // ── FOOTER ──────────────────────────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    applyDraw(doc, RULE);
    doc.setLineWidth(0.4);
    doc.line(ml, H - 28, mr, H - 28);
    applyColor(doc, MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text('SermonBuild', ml, H - 18);
    doc.text(`${i} / ${pages}`, W / 2, H - 18, { align: 'center' });
  }

  return doc.output('blob');
}
